export interface RhOptionQuote {
  mid: number;
  popShort: number | null;
  popLong: number | null;
}

function readQuoteNumber(quote: Record<string, unknown>, key: string): number | null {
  const value = quote[key];
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function firstQuoteNumber(quote: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = readQuoteNumber(quote, key);
    if (value != null) return value;
  }
  return null;
}

/** Bid/ask midpoint when available, else mark, else last — same chain as screeners. */
export function resolveRhOptionMid(fields: Record<string, unknown>): number | null {
  const bid = firstQuoteNumber(fields, ["bid_price", "bid"]);
  const ask = firstQuoteNumber(fields, ["ask_price", "ask"]);
  if (bid != null && ask != null && bid >= 0 && ask >= bid && (bid > 0 || ask > 0)) {
    const spreadMid = (bid + ask) / 2;
    if (spreadMid > 0) return spreadMid;
  }

  const explicitMid = firstQuoteNumber(fields, ["mid_price", "mid"]);
  if (explicitMid != null && explicitMid > 0) return explicitMid;

  const mark =
    firstQuoteNumber(fields, ["adjusted_mark_price", "mark_price"]) ??
    firstQuoteNumber(fields, ["mark"]);
  if (mark != null && mark > 0) return mark;

  const last = firstQuoteNumber(fields, ["last_trade_price", "last"]);
  if (last != null && last > 0) return last;

  return null;
}

export function parseRhOptionQuoteEntry(entry: unknown): RhOptionQuote | null {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null;
  const quote = (entry as { quote?: unknown }).quote;
  if (!quote || typeof quote !== "object" || Array.isArray(quote)) return null;
  const q = quote as Record<string, unknown>;
  const mid = resolveRhOptionMid(q);
  if (mid == null) return null;
  return {
    mid,
    popShort: readQuoteNumber(q, "chance_of_profit_short"),
    popLong: readQuoteNumber(q, "chance_of_profit_long"),
  };
}

export function parseRhOptionQuoteMap(results: unknown): Map<string, RhOptionQuote> {
  const map = new Map<string, RhOptionQuote>();
  if (!Array.isArray(results)) return map;
  for (const entry of results) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const instrumentIdRaw = (entry as { quote?: { instrument_id?: unknown } }).quote?.instrument_id;
    const instrumentId = typeof instrumentIdRaw === "string" ? instrumentIdRaw.trim() : "";
    const parsed = parseRhOptionQuoteEntry(entry);
    if (instrumentId && parsed) map.set(instrumentId, parsed);
  }
  return map;
}

export function rhOptionPopForSide(quote: RhOptionQuote, isShort: boolean): number | null {
  const pop = isShort ? quote.popShort : quote.popLong;
  return pop != null && Number.isFinite(pop) ? pop : null;
}

export function computeRhOptionPnl(
  entry: number | null | undefined,
  mid: number | null | undefined,
  contracts: number,
  isShort: boolean,
): { profit: number | null; profitPct: number | null } {
  if (entry == null || mid == null || !Number.isFinite(entry) || !Number.isFinite(mid) || !(contracts > 0)) {
    return { profit: null, profitPct: null };
  }
  const mult = 100 * contracts;
  const profit = isShort ? (entry - mid) * mult : (mid - entry) * mult;
  const basis = entry * mult;
  const profitPct = basis > 0 ? (profit / basis) * 100 : null;
  return { profit, profitPct };
}
